import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { AdditionalClientConsentFile } from '@/components/misc/ClientIntroductionAdditionalPdfs/AdditionalConsentFiles'
import { AddServiceFeeFormValues } from '@/components/misc/CommercialAgreementsForm/components/AddServiceFeeModal/constants'
import {
  COMMERCIAL_AGREEMENT_FIELDS,
  COMMERCIAL_AGREEMENT_TYPE_OPTION,
} from '@/components/misc/CommercialAgreementsForm/constants'
import { COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION } from '@/lib/constants'
import commercialAgreementService from '@/lib/services/commercialAgreementService'
import {
  SERVICE_AREA_OPTION_ALL,
  SERVICE_AREA_OPTION_OTHER,
} from '@/lib/services/serviceFeeService'
import { RootState } from '@/redux/store'
import {
  CommercialAgreementReferralDirection,
  CommercialAgreementType,
  SerializableFile,
} from '@/types/misc'
import { CommercialAgreementItem } from '@/types/responses/commercial-agreements'

export type CommercialAgreementsFormState = {
  referralDirection: CommercialAgreementReferralDirection
  form: CommercialAgreementsFormValues
  hasCreatedOutgoingAgreement: boolean
  // This is set in useHandleAddOrUpdateAgreementError
  serviceFeeErrors?: ServiceFeeErrors
}

export type ServiceFeeErrors = Record<string, string[]>

export interface CommercialAgreementsFormValues {
  hasCommercialAgreement?: boolean
  outgoingAgreement: CommercialAgreementDirectionInput
  incomingAgreement: CommercialAgreementDirectionInput
}

export interface CommercialAgreementDirectionInput {
  type?: CommercialAgreementTypeOption
  existingAgreementFile?: SerializableFile | null
  serviceFees: AddServiceFeeFormValues[]
  consentLetterApprovedAt?: string
  consentLetterAmendRequest?: string
  commissionJustification?: string
  hasEditedCommissionJustification?: boolean
  isRqHandleFeeSharing?: boolean
  previousConsentFiles: Array<CommercialAgreementItem['consentFiles'][number]>
  newConsentFiles: AdditionalClientConsentFile[]
  // The IRI can be for both CommercialAgreementConsentFile or MediaObject records
  consentFilesOrder: IRI[]
}

export interface ServiceFeeInput {
  serviceArea?:
    | IRI
    | typeof SERVICE_AREA_OPTION_ALL
    | typeof SERVICE_AREA_OPTION_OTHER
  serviceAreaOther: string
  feeCategory?: string
  feeCategoryOther: string
  shareType: string
  shareValue?: number
  shareValueMax?: number
  duration?: string
  notes: string
  example: string
  defaultIntroductionExample: string
  defaultRetrospectiveExample: string
  groupIntroductionExample: string
  groupRetrospectiveExample: string
  passBackFee?: boolean
  passBackFeeNotes?: string
  existingServiceFeeId?: number
  isFromCompany: boolean
  willAddAnother: boolean
}

export type CommercialAgreementTypeOption = CommercialAgreementType | 'LATER'

export const COMMERCIAL_AGREEMENT_DIRECTION_INITIAL_INPUT: CommercialAgreementDirectionInput =
  {
    serviceFees: [],
    existingAgreementFile: null,
    type: COMMERCIAL_AGREEMENT_TYPE_OPTION.LATER,
    hasEditedCommissionJustification: false,
    previousConsentFiles: [],
    newConsentFiles: [],
    consentFilesOrder: [],
  }

export const INITIAL_COMMERCIAL_AGREEMENTS_FORM_STATE: CommercialAgreementsFormState =
  {
    referralDirection: COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING,
    form: {
      outgoingAgreement: COMMERCIAL_AGREEMENT_DIRECTION_INITIAL_INPUT,
      incomingAgreement: COMMERCIAL_AGREEMENT_DIRECTION_INITIAL_INPUT,
    },
    hasCreatedOutgoingAgreement: false,
  }

export const commercialAgreementsForm = createSlice({
  name: 'commercialAgreementsForm',
  initialState: INITIAL_COMMERCIAL_AGREEMENTS_FORM_STATE,
  reducers: {
    setReferralDirection(
      state,
      action: PayloadAction<CommercialAgreementsFormState['referralDirection']>,
    ) {
      state.referralDirection = action.payload
    },

    setHasCreatedOutgoingAgreement(
      state,
      action: PayloadAction<
        CommercialAgreementsFormState['hasCreatedOutgoingAgreement']
      >,
    ) {
      state.hasCreatedOutgoingAgreement = action.payload
    },

    setCommercialAgreementFormValues(
      state,
      action: PayloadAction<CommercialAgreementsFormState['form']>,
    ) {
      state.form = action.payload
    },

    clearCommercialAgreementFormValues(state) {
      state.form = INITIAL_COMMERCIAL_AGREEMENTS_FORM_STATE.form
    },

    clearCommercialAgreementsForm() {
      return INITIAL_COMMERCIAL_AGREEMENTS_FORM_STATE
    },

    setServiceFeeErrors(
      state,
      action: PayloadAction<CommercialAgreementsFormState['serviceFeeErrors']>,
    ) {
      state.serviceFeeErrors = action.payload
    },

    clearServiceFeeErrors(state) {
      state.serviceFeeErrors =
        INITIAL_COMMERCIAL_AGREEMENTS_FORM_STATE.serviceFeeErrors
    },
  },
})

// Selectors
export const selectCommercialAgreementsForm = (state: RootState) =>
  state.commercialAgreementsForm

export const selectCommercialAgreementsFormValues = createSelector(
  selectCommercialAgreementsForm,
  (state) => state.form,
)

export const selectReferralDirection = createSelector(
  selectCommercialAgreementsForm,
  (state) => state.referralDirection,
)

export const selectHasCreatedOutgoingAgreement = createSelector(
  selectCommercialAgreementsForm,
  (state) => state.hasCreatedOutgoingAgreement,
)

export const selectIsOutgoingAgreement = createSelector(
  selectReferralDirection,
  (direction) => direction === COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.OUTGOING,
)

export const selectIsIncomingAgreement = createSelector(
  selectReferralDirection,
  (direction) => direction === COMMERCIAL_AGREEMENT_REFERRAL_DIRECTION.INCOMING,
)

export const selectCurrentDirectionValues = createSelector(
  selectCommercialAgreementsFormValues,
  selectReferralDirection,
  (formValues, referralDirection) => {
    return commercialAgreementService.isOutgoingAgreementDirection(
      referralDirection,
    )
      ? formValues.outgoingAgreement
      : formValues.incomingAgreement
  },
)

export const selectCurrentDirectionAgreementType = createSelector(
  selectCurrentDirectionValues,
  (values) => values.type,
)

export const selectServiceFeeErrors = createSelector(
  selectCommercialAgreementsForm,
  (state) => state.serviceFeeErrors,
)

export const selectHasAgreementForCurrentDirection = createSelector(
  selectCurrentDirectionAgreementType,
  (type) => commercialAgreementService.hasCommercialAgreement(type),
)

export const selectCurrentAgreementPrefix = createSelector(
  selectIsOutgoingAgreement,
  (isOutgoingAgreement) =>
    isOutgoingAgreement
      ? COMMERCIAL_AGREEMENT_FIELDS.outgoingAgreement
      : COMMERCIAL_AGREEMENT_FIELDS.incomingAgreement,
)

// Actions
export const {
  setReferralDirection,
  setCommercialAgreementFormValues,
  clearCommercialAgreementFormValues,
  clearCommercialAgreementsForm,
  setServiceFeeErrors,
  clearServiceFeeErrors,
} = commercialAgreementsForm.actions

export const commercialAgreementsFormReducer = commercialAgreementsForm.reducer
